import { useMemo } from "react";
import DataTable from "react-data-table-component";
import { Button, DefaultComponentProps, Dialog, DialogActions, DialogContent, DialogTitle, FlexGrid, Typography } from '@albo-ui/core';
import { Box, Chip } from "@mui/material";
import { customStyles4 } from "../../../util/TableStyles";
import { noDataComponent } from "../../../util/DataObject";
import { buildManualCheckDetails, getBlacklistFullName, getBlacklistTaxId, getManualCheck, isManualCleared, isManualRejected, typeBlackList } from "./pldStatus";

// Re-export para mantener la API pública existente
export { typeBlackList } from "./pldStatus";

export type ModalBlackListDialogProps = {
    onClose: () => void;
    open: boolean;
    blackList: Array<any>;
    title?: string;
    showManualCheck?: boolean;
    onManualCheck?: (item: any) => void;
} & DefaultComponentProps;

type MatchChip = {
    label: string;
    backgroundColor: string;
    color: string;
    fontWeight: number;
};

const RESULT_CHIPS: Record<"error" | "matched" | "no_match" | "approved" | "rejected", MatchChip> = {
    error: { label: "No se realizó", backgroundColor: "#FFE3E3", color: "#C92A2A", fontWeight: 600 },
    matched: { label: "Con coincidencia", backgroundColor: "#FFE3E3", color: "#C92A2A", fontWeight: 600 },
    no_match: { label: "Sin coincidencia", backgroundColor: "#F1F3F5", color: "#495057", fontWeight: 500 },
    approved: { label: "Cruce aprobado", backgroundColor: "#E6FCF5", color: "#0CA678", fontWeight: 600 },
    rejected: { label: "Cruce NO aprobado", backgroundColor: "#FFE3E3", color: "#C92A2A", fontWeight: 600 },
};

const getResultChip = (row: any): MatchChip => {
    if (row?.status === "error") return RESULT_CHIPS.error;
    if (row?.result !== "matched") return RESULT_CHIPS.no_match;

    // Coincidencia ya validada en cruce manual: aprobado (verde) / no aprobado (rojo)
    const manualCheck = getManualCheck(row);
    if (isManualCleared(manualCheck)) return RESULT_CHIPS.approved;
    if (isManualRejected(manualCheck)) return RESULT_CHIPS.rejected;

    return RESULT_CHIPS.matched;
};

const ResultBadge = ({ row }: { row: any }) => {
    const chip = getResultChip(row);
    const url = row?.url;
    const linkProps = url
        ? { component: "a" as const, href: url, target: "_blank", rel: "noopener noreferrer", clickable: true }
        : {};
    return (
        <Chip
            label={chip.label}
            size="small"
            {...linkProps}
            sx={{
                backgroundColor: chip.backgroundColor,
                color: chip.color,
                fontWeight: chip.fontWeight,
                fontSize: "0.75rem",
                ...(url ? { textDecoration: "underline", cursor: "pointer" } : {}),
            }}
        />
    );
};

const LEGENDS = [
    {
        color: "#2ECC71",
        textColor: "#27AE60",
        title: "Sin coincidencia",
        description: "El nombre no coincide con ninguna lista. El onboarding continúa y el estatus puede avanzar a Contrato enviado.",
    },
    {
        color: "#E67E22",
        textColor: "#D35400",
        title: "Inexacta → cruce manual obligatorio",
        description: 'Match de nombre sin segundo identificador. Manual check registra literalmente "hubo coincidencia" o "no hubo coincidencia". Sin ese registro no se crea la cuenta ni se cambia el estatus.',
    },
    {
        color: "#E74C3C",
        textColor: "#C0392B",
        title: "Exacta o coincidencia confirmada",
        description: "Se confirma la identidad: la cuenta no debe crearse, se genera alerta y evidencia y Contrato enviado queda bloqueada.",
    },
];

const buildColumns = (showManualCheck: boolean, onManualCheck?: (item: any) => void): any[] => {
    const columns: any[] = [
        { selector: (row: any) => typeBlackList[row.type] || row.type || "Entidad", name: "Tipo", wrap: true, sortable: false },
        { selector: (row: any) => getBlacklistFullName(row), name: "Nombre completo", wrap: true, sortable: false, grow: 1.5 },
        { selector: (row: any) => getBlacklistTaxId(row), name: "RFC / CURP", wrap: true, sortable: false },
        { selector: (row: any) => <ResultBadge row={row} />, name: "Resultado", wrap: true, sortable: false },
    ];

    if (showManualCheck) {
        columns.push({
            name: "Acción",
            sortable: false,
            cell: (row: any) => {
                const hasManualCheck = !!(row.manualcheck || row.manualCheck);
                const canPerformManualCheck = row?.result === "matched" || hasManualCheck;
                if (!canPerformManualCheck) return "---";

                return (
                    <Typography
                        variant="paragraph"
                        style={{
                            color: hasManualCheck ? "#275daeff" : "#D35400",
                            textDecoration: "underline",
                            cursor: "pointer",
                            fontSize: "0.82rem",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                        }}
                        onClick={() => onManualCheck?.(buildManualCheckDetails(row))}
                    >
                        {hasManualCheck ? "Editar cruce manual" : "Cruce manual"}
                    </Typography>
                );
            },
        });
    }

    return columns;
};

export const ModalBlackListDialog = ({
    onClose,
    open,
    blackList,
    title,
    showManualCheck = false,
    onManualCheck,
}: ModalBlackListDialogProps) => {
    const columns = useMemo(() => buildColumns(showManualCheck, onManualCheck), [showManualCheck, onManualCheck]);

    return (
        <Dialog onClose={onClose} open={open} style={{ width: 1000, maxWidth: "100vw", maxHeight: 600 }}>
            <DialogTitle>
                <Typography variant="h2" style={{ fontSize: "1.25rem", fontWeight: 700 }}>
                    {title}
                </Typography>
                <Box sx={{ mb: 1 }}>
                    <Typography variant="paragraph" color="lightNeutral" style={{ fontSize: "0.85rem", marginTop: 4 }}>
                        Las coincidencias inexactas requieren cruce manual de Manual Check
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent>
                <FlexGrid fullWidth direction="column" alignItems="flex-start" spacing={3}>
                    <FlexGrid container spacing={2}>
                        <DataTable
                            columns={columns}
                            data={blackList}
                            customStyles={customStyles4}
                            noDataComponent={noDataComponent}
                            dense
                        />
                    </FlexGrid>

                    {/* Matriz de decisión PLD */}
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2, width: "100%", mt: 5 }}>
                        {LEGENDS.map(({ color, textColor, title: legendTitle, description }) => (
                            <Box key={legendTitle} sx={{ borderTop: `4px solid ${color}`, pt: 1.5, px: 1 }}>
                                <Typography variant="smallTitle" style={{ fontWeight: 700, color: textColor, fontSize: "0.9rem" }}>
                                    {legendTitle}
                                </Typography>
                                <Typography variant="paragraph" color="lightNeutral" style={{ fontSize: "0.8rem", marginTop: 4, display: "block" }}>
                                    {description}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </FlexGrid>
            </DialogContent>

            <DialogActions>
                <FlexGrid container fullWidth justify="center" alignItems="center" style={{ marginTop: '16px' }}>
                    <Button onClick={onClose} size="normal">
                        Entendido
                    </Button>
                </FlexGrid>
            </DialogActions>
        </Dialog>
    );
};
